/**
 * Minimal structural shape of a parent-model relationship needed to decide
 * whether it should be offered in the filter picker. Matches the
 * `RelationshipWithoutFilterableColumns` GraphQL fragment.
 */
export type FilterableRelationship = {
  id: string | number;
  is_hidden_from_audience_filter?: boolean | null;
  through_relationships: unknown[];
  to_model: {
    event?: {
      realtime_event_source_id?: string | number | null;
    } | null;
    cep_managed_model?: {
      type: string;
    } | null;
  };
};

export type RelationshipFilterContext = {
  isAudienceCreated: boolean;
  enableRealtime: boolean;
  restrictToPredictorKeyEvents: boolean;
  predictorKeyEvents: Set<string>;
};

/**
 * Decides whether a relationship (to a related model or event model) should be
 * offered in the audience builder's filter picker.
 *
 * This filtering exists for UX reasons and not governance, so there's no
 * "data leak" by doing it client-side. It may be good to do this on the
 * GraphQL side instead so we fetch less resources, but doing it here keeps
 * the filtering in one place instead of applying it to multiple queries.
 */
export const shouldShowRelationship = (
  relationship: FilterableRelationship,
  context: RelationshipFilterContext,
): boolean => {
  if (relationship.is_hidden_from_audience_filter) {
    return false;
  }

  if (relationship.to_model.event?.realtime_event_source_id != null) {
    // Streaming evaluation only supports direct parent → event relationships:
    // the event router resolves user identity via direct_model_relationships
    // and never routes events across through-relationships, so a realtime
    // event condition on a through-relationship would never be evaluated.
    if (relationship.through_relationships.length !== 0) {
      return false;
    }

    // Filter out realtime relationships if the audience has been created and
    // it's not realtime.
    return context.isAudienceCreated ? context.enableRealtime : true;
  }

  if (context.restrictToPredictorKeyEvents) {
    // Predictive traits currently support only directly related event models
    if (
      !relationship.to_model.event ||
      relationship.through_relationships.length !== 0
    ) {
      return false;
    }

    // Predictors only allow key events defined in their feature definitions
    if (!context.predictorKeyEvents.has(relationship.id.toString())) {
      return false;
    }
  }

  return true;
};
